import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  faUser,
  faMapMarkerAlt,
  faRoute,
  faBullseye,
  faMountain,
  faHeart,
  faPenToSquare,
  faFloppyDisk,
  faSpinner,
  faGlobe,
  faPhone,
  faLightbulb,
  faStar,
  faTags
} from "@fortawesome/free-solid-svg-icons";

import {
  faInstagram,
  faFacebook,
  faLinkedin,
  faXTwitter,
  faYoutube,
} from "@fortawesome/free-brands-svg-icons";

import useFetch from "../../hooks/useFetch";
import FoundHerPDFProfile from "./modules/foundher/FoundHerPDFProfile";

const FoundHerProfile = () => {
  const authState = useSelector((state) => state.authReducer || {});
  const [fetchData] = useFetch();

  const token = authState?.token;
  const userId = authState?.user?._id || "";
  const userCode = authState?.user?.think5whyscode || "";
  const userName = authState?.user?.name || "";

  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isUpdated, setIsUpdated] = useState(false);
  const [msg, setMsg] = useState("");
  const [editId, setEditId] = useState(null);

  const [showJourney, setShowJourney] = useState(false);
  const [showDreams, setShowDreams] = useState(false);
  const [showPurpose, setShowPurpose] = useState(false);
  const [showChallenges, setShowChallenges] = useState(false);

  const [showInnovations, setShowInnovations] = useState(false);
  const [showBrands, setShowBrands] = useState(false);
  const [showSpotlights, setShowSpotlights] = useState(false);

  const [showPDFGenerator, setShowPDFGenerator] = useState(false);
  const [currentFormData, setCurrentFormData] = useState(null);

  const [form, setForm] = useState({
    userId,
    userCode,
    userName,
    address: "",
    journeySoFar: "",
    dreams: "",
    purpose: "",
    challenges: "",
    socialProfiles: {
      instagram: "",
      facebook: "",
      linkedin: "",
      x: "",
      youtube: "",
      whatsapp: "",
      website: "",
    },
    profileCompletion: 0,
    isPublished: false,
  });

  const buildFilledForm = (record) => ({
    userId: record?.userId || userId,
    userCode: record?.userCode || userCode,
    userName: record?.userName || userName,
    address: record?.address || "",
    journeySoFar: record?.journeySoFar || "",
    dreams: record?.dreams || "",
    innovations: record?.innovations || "",
    brands: record?.brands || "",
    spotlights: record?.spotlights || "",
    purpose: record?.purpose || "",
    challenges: record?.challenges || "",
    socialProfiles: {
      instagram: record?.socialProfiles?.instagram || "",
      facebook: record?.socialProfiles?.facebook || "",
      linkedin: record?.socialProfiles?.linkedin || "",
      x: record?.socialProfiles?.x || "",
      youtube: record?.socialProfiles?.youtube || "",
      whatsapp: record?.socialProfiles?.whatsapp || "",
      website: record?.socialProfiles?.website || "",
    },
    profileCompletion: record?.profileCompletion || 0,
    isPublished: record?.isPublished || false,
  });

  const fetchMyProfile = async () => {
    try {
      setLoading(true);
      setMsg("");

      const config = {
        url: "/foundher/profile/my",
        method: "post",
        headers: { Authorization: token },
        data: { userId },
      };

      const response = await fetchData(config);

      if (response?.status && response?.record) {
        setForm(buildFilledForm(response.record));
        setEditId(response.record._id);
      } else {
        setForm(buildFilledForm({}));
        setEditId(null);
      }
    } catch (err) {
      console.error(err);
      setMsg("Failed to load profile.");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    try {
      setLoading(true);
      setMsg("");

      const config = {
        url: "/foundher/profile/update",
        method: "PUT",
        headers: { Authorization: token },
        data: { ...form },
      };

      const response = await fetchData(config);

      if (response?.status) {
        setMsg("✅ FoundHer Profile updated successfully.");
        setForm(buildFilledForm(response.record));
        setEditId(response.record._id);
        setIsUpdated(true);
        setIsEditing(false);
      } else {
        setMsg(response?.msg || "Unable to update profile.");
        setIsUpdated(false);
      }
    } catch (err) {
      console.error(err);
      setMsg("Server Error.");
      setIsUpdated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // Fetch profile on mount
  useEffect(() => {
    if (userId) {
      fetchMyProfile();
    }
  }, [userId]);

  if (loading) {
    return (
      <div className="bg-gray-900 p-5 rounded-xl text-white flex items-center">
        <FontAwesomeIcon icon={faSpinner} spin className="mr-3 text-cyan-300" />
        Loading FoundHer Profile...
      </div>
    );
  }

  const handleDownloadProfile = () => {
    const filledForm = buildFilledForm(form);
    setCurrentFormData(filledForm);
    setShowPDFGenerator(true);
  };

  return (
    <div className="space-y-6 bg-gray-900 p-6 rounded-xl text-white border-t-2 border-pink-400 mt-2">
      {msg && (
        <div className="text-sm text-green-400 bg-gray-800 p-3 rounded-lg">
          {msg}
        </div>
      )}

      <div className="bg-gray-800 p-4 rounded-xl border-t border-pink-300">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg text-pink-300 font-semibold">
            <FontAwesomeIcon icon={faUser} className="mr-2" />
            My FoundHer Profile
          </h3>
        </div>
        <div className="flex items-start mb-5">
          <button
            onClick={() => setIsEditing(true)}
            disabled={isEditing}
            className="bg-pink-600 hover:bg-pink-700 w-fit px-4 py-2 rounded-md text-sm disabled:opacity-50 transition-colors"
          >
            <FontAwesomeIcon icon={faPenToSquare} className="mr-2" />
            Enable Editing
          </button>
          <button
            onClick={handleDownloadProfile}
            className="bg-pink-600 hover:bg-pink-700 w-fit px-4 py-2 rounded-md text-sm disabled:opacity-50 transition-colors"
          >
            Download Profile
          </button>
        </div>

        {showPDFGenerator && currentFormData && (
          <FoundHerPDFProfile
            formData={currentFormData}
            authData={authState?.user || {}}
            onDownloadComplete={() => {
              setShowPDFGenerator(false);
              setCurrentFormData(null);
            }}
          />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <label className="text-xs text-gray-300 block mb-1">
              User Code
            </label>
            <input
              className="w-full bg-gray-700 rounded-md p-3 text-sm"
              value={form.userCode}
              disabled
            />
          </div>

          <div>
            <label className="text-xs text-gray-300 block mb-1">
              User Name
            </label>
            <input
              className="w-full bg-gray-700 rounded-md p-3 text-sm"
              value={form.userName}
              disabled
            />
          </div>

          <div className="lg:col-span-2">
            <label className="text-xs text-gray-300 block mb-1">
              <FontAwesomeIcon
                icon={faMapMarkerAlt}
                className="mr-2 text-yellow-300"
              />
              Address
            </label>
            <input
              disabled={!isEditing}
              value={form.address}
              onChange={(e) => handleChange("address", e.target.value)}
              className="w-full bg-gray-700 rounded-md p-3 text-sm disabled:opacity-75"
            />
          </div>

          <div className="lg:col-span-2 border-t border-pink-300 pt-5">
            <h4 className="text-pink-300 font-semibold mb-4">
              Social Profiles
            </h4>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-300 block mb-1">
                  <FontAwesomeIcon
                    icon={faInstagram}
                    className="mr-2 text-pink-400"
                  />
                  Instagram
                </label>
                <input
                  disabled={!isEditing}
                  value={form.socialProfiles?.instagram || ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      socialProfiles: {
                        ...form.socialProfiles,
                        instagram: e.target.value,
                      },
                    })
                  }
                  className="w-full bg-gray-700 rounded-md p-3 text-sm"
                />
              </div>

              <div>
                <label className="text-xs text-gray-300 block mb-1">
                  <FontAwesomeIcon
                    icon={faFacebook}
                    className="mr-2 text-blue-400"
                  />
                  Facebook
                </label>
                <input
                  disabled={!isEditing}
                  value={form.socialProfiles?.facebook || ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      socialProfiles: {
                        ...form.socialProfiles,
                        facebook: e.target.value,
                      },
                    })
                  }
                  className="w-full bg-gray-700 rounded-md p-3 text-sm"
                />
              </div>

              <div>
                <label className="text-xs text-gray-300 block mb-1">
                  <FontAwesomeIcon
                    icon={faLinkedin}
                    className="mr-2 text-cyan-400"
                  />
                  LinkedIn
                </label>
                <input
                  disabled={!isEditing}
                  value={form.socialProfiles?.linkedin || ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      socialProfiles: {
                        ...form.socialProfiles,
                        linkedin: e.target.value,
                      },
                    })
                  }
                  className="w-full bg-gray-700 rounded-md p-3 text-sm"
                />
              </div>

              <div>
                <label className="text-xs text-gray-300 block mb-1">
                  <FontAwesomeIcon
                    icon={faXTwitter}
                    className="mr-2 text-white"
                  />
                  X
                </label>
                <input
                  disabled={!isEditing}
                  value={form.socialProfiles?.x || ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      socialProfiles: {
                        ...form.socialProfiles,
                        x: e.target.value,
                      },
                    })
                  }
                  className="w-full bg-gray-700 rounded-md p-3 text-sm"
                />
              </div>

              <div>
                <label className="text-xs text-gray-300 block mb-1">
                  <FontAwesomeIcon
                    icon={faYoutube}
                    className="mr-2 text-red-500"
                  />
                  YouTube
                </label>
                <input
                  disabled={!isEditing}
                  value={form.socialProfiles?.youtube || ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      socialProfiles: {
                        ...form.socialProfiles,
                        youtube: e.target.value,
                      },
                    })
                  }
                  className="w-full bg-gray-700 rounded-md p-3 text-sm"
                />
              </div>

              <div>
                <label className="text-xs text-gray-300 block mb-1">
                  <FontAwesomeIcon
                    icon={faPhone}
                    className="mr-2 text-green-400"
                  />
                  WhatsApp
                </label>
                <input
                  disabled={!isEditing}
                  value={form.socialProfiles?.whatsapp || ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      socialProfiles: {
                        ...form.socialProfiles,
                        whatsapp: e.target.value,
                      },
                    })
                  }
                  className="w-full bg-gray-700 rounded-md p-3 text-sm"
                />
              </div>

              <div className="lg:col-span-2">
                <label className="text-xs text-gray-300 block mb-1">
                  <FontAwesomeIcon
                    icon={faGlobe}
                    className="mr-2 text-yellow-300"
                  />
                  Website
                </label>
                <input
                  disabled={!isEditing}
                  value={form.socialProfiles?.website || ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      socialProfiles: {
                        ...form.socialProfiles,
                        website: e.target.value,
                      },
                    })
                  }
                  className="w-full bg-gray-700 rounded-md p-3 text-sm"
                />
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <button
              type="button"
              onClick={() => setShowJourney(!showJourney)}
              className="w-full text-left bg-cyan-900 hover:bg-cyan-800 rounded-md px-3 py-2 text-sm font-medium"
            >
              <FontAwesomeIcon icon={faRoute} className="mr-2 text-cyan-300" />
              Journey So Far {showJourney ? "▲" : "▼"}
            </button>

            {showJourney && (
              <textarea
                rows={6}
                disabled={!isEditing}
                value={form.journeySoFar}
                onChange={(e) => handleChange("journeySoFar", e.target.value)}
                className="mt-2 w-full bg-gray-700 rounded-md p-3 text-sm disabled:opacity-75"
              />
            )}
          </div>
          <div className="lg:col-span-2">
            <button
              type="button"
              onClick={() => setShowDreams(!showDreams)}
              className="w-full text-left bg-green-900 hover:bg-green-800 rounded-md px-3 py-2 text-sm font-medium"
            >
              <FontAwesomeIcon
                icon={faBullseye}
                className="mr-2 text-green-300"
              />
              Dreams {showDreams ? "▲" : "▼"}
            </button>

            {showDreams && (
              <textarea
                rows={5}
                disabled={!isEditing}
                value={form.dreams}
                onChange={(e) => handleChange("dreams", e.target.value)}
                className="mt-2 w-full bg-gray-700 rounded-md p-3 text-sm disabled:opacity-75"
              />
            )}
          </div>
          <div className="lg:col-span-2">
            <button
              type="button"
              onClick={() => setShowPurpose(!showPurpose)}
              className="w-full text-left bg-pink-900 hover:bg-pink-800 rounded-md px-3 py-2 text-sm font-medium"
            >
              <FontAwesomeIcon icon={faHeart} className="mr-2 text-pink-300" />
              Purpose {showPurpose ? "▲" : "▼"}
            </button>

            {showPurpose && (
              <textarea
                rows={5}
                disabled={!isEditing}
                value={form.purpose}
                onChange={(e) => handleChange("purpose", e.target.value)}
                className="mt-2 w-full bg-gray-700 rounded-md p-3 text-sm disabled:opacity-75"
              />
            )}
          </div>
          <div className="lg:col-span-2">
            <button
              type="button"
              onClick={() => setShowChallenges(!showChallenges)}
              className="w-full text-left bg-orange-900 hover:bg-orange-800 rounded-md px-3 py-2 text-sm font-medium"
            >
              <FontAwesomeIcon
                icon={faMountain}
                className="mr-2 text-orange-300"
              />
              Challenges {showChallenges ? "▲" : "▼"}
            </button>

            {showChallenges && (
              <textarea
                rows={5}
                disabled={!isEditing}
                value={form.challenges}
                onChange={(e) => handleChange("challenges", e.target.value)}
                className="mt-2 w-full bg-gray-700 rounded-md p-3 text-sm disabled:opacity-75"
              />
            )}
          </div>

          <div className="lg:col-span-2">
            <button
              type="button"
              onClick={() => setShowInnovations(!showInnovations)}
              className="w-full text-left bg-green-900 hover:bg-green-800 rounded-md px-3 py-2 text-sm font-medium"
            >
              <FontAwesomeIcon
                icon={faLightbulb}
                className="mr-2 text-green-300"
              />
              Innovations {showInnovations ? "▲" : "▼"}
            </button>

            {showInnovations && (
              <textarea
                rows={5}
                disabled={!isEditing}
                value={form.innovations}
                onChange={(e) => handleChange("innovations", e.target.value)}
                className="mt-2 w-full bg-gray-700 rounded-md p-3 text-sm disabled:opacity-75"
              />
            )}
          </div>

          <div className="lg:col-span-2">
            <button
              type="button"
              onClick={() => setShowBrands(!showBrands)}
              className="w-full text-left bg-green-900 hover:bg-green-800 rounded-md px-3 py-2 text-sm font-medium"
            >
              <FontAwesomeIcon
                icon={faTags}
                className="mr-2 text-green-300"
              />
              Brands {showBrands ? "▲" : "▼"}
            </button>

            {showBrands && (
              <textarea
                rows={5}
                disabled={!isEditing}
                value={form.brands}
                onChange={(e) => handleChange("brands", e.target.value)}
                className="mt-2 w-full bg-gray-700 rounded-md p-3 text-sm disabled:opacity-75"
              />
            )}
          </div>

          <div className="lg:col-span-2">
            <button
              type="button"
              onClick={() => setShowSpotlights(!showSpotlights)}
              className="w-full text-left bg-green-900 hover:bg-green-800 rounded-md px-3 py-2 text-sm font-medium"
            >
              <FontAwesomeIcon
                icon={faStar}
                className="mr-2 text-green-300"
              />
              Spotlights {showSpotlights ? "▲" : "▼"}
            </button>

            {showSpotlights && (
              <textarea
                rows={5}
                disabled={!isEditing}
                value={form.spotlights}
                onChange={(e) => handleChange("spotlights", e.target.value)}
                className="mt-2 w-full bg-gray-700 rounded-md p-3 text-sm disabled:opacity-75"
              />
            )}
          </div>
          <div className="flex items-center justify-between bg-gray-800 rounded-md p-3 mt-4">
            <div>
              <p className="text-sm font-medium text-cyan-200">Public Profile</p>
              <p className="text-xs text-gray-400">
                {form.isPublished ? "Visible to anyone who scans your QR code" : "Hidden — your QR code won't show your profile yet"}
              </p>
            </div>
            <button
              type="button"
              disabled={!isEditing}
              onClick={() => handleChange("isPublished", !form.isPublished)}
              className={`w-12 h-6 rounded-full transition-colors relative disabled:opacity-50 ${
                form.isPublished ? "bg-teal-600" : "bg-gray-600"
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                  form.isPublished ? "translate-x-6" : ""
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 pt-4">
        <button
          onClick={handleUpdate}
          disabled={!isEditing || loading}
          className="flex-1 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-600 w-fit px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center"
        >
          <FontAwesomeIcon icon={faFloppyDisk} className="mr-2" />
          Update Profile
        </button>

        <button
          onClick={() => {
            setIsEditing(false);
            fetchMyProfile();
          }}
          disabled={loading}
          className="flex-1 bg-gray-600 hover:bg-gray-700 w-fit px-3 py-2 rounded-md text-sm font-medium transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default FoundHerProfile;
